import { t } from '../i18n';
import { LAUNCHER } from './component-contract';

export const defaultConfiguration = {
    titleClosed: t('brand.default_title'),
    clickButton: t('common.send'),
    titleOpen: t('brand.default_subtitle'),
    closedStyle: 'button', // button or chat
    closedChatAvatarUrl: '', // only used if closedStyle is set to 'chat'
    cookieExpiration: 1, // in days. Once opened, closed chat title will be shown as button (when closedStyle is set to 'chat')
    introMessage: t('brand.default_welcome'),
    autoResponse: t('status.connecting'),
    autoNoResponse: t('status.offline_queued') +
    '',
    placeholderText: t('input.placeholder'),
    displayMessageTime: true,
    // 主题颜色配置 - 现代扁平风格
    mainColor: '#2563eb',
    // headerGradient 特意不在这里给静态值——它现在是 mainColor 的派生量
    // （resolveHeaderGradient，本文件下方），出厂蓝的这条渐变也是靠派生算出来的，
    // 不是硬编码。这里如果写死一个值，会导致 widget.js 构造函数里没法用
    // hasOwnProperty(conf,'headerGradient') 区分"集成方真的传了 headerGradient"
    // 还是"只是这份 default 对象自带的"——那正是 fix round 1 之前的 bug 根源
    // （dash 配了 mainColor，header 渐变纹丝不动，因为它读的是这个写死的常量）。
    secondaryColor: '#f1f5f9',
    textColor: '#1e293b',
    chatBubbleColor: '#f1f5f9',
    chatBubbleTextColor: '#1e293b',
    userBubbleColor: '#2563eb',
    userBubbleTextColor: '#ffffff',
    headerTextColor: '#ffffff',
    inputBorderColor: '#e2e8f0',
    // 位置配置。桌面两条走组件契约表（macOS SDK 的 factoryMargin 是同一个数）；
    // 移动两条是本端自己的 18，不进跨端表。
    btnPcBottom: `${LAUNCHER.marginBottomPx}px`,
    btnPcRight: `${LAUNCHER.marginRightPx}px`,
    btnMobileBottom: '18px',
    btnMobileRight: '18px',
    // 启动器图标风格（widget_config.launcher_icon_style）。出厂 duotone，
    // 值域与归一都在契约表里（LAUNCHER.iconStyles / normalizeLauncherIconStyle）。
    launcherIconStyle: LAUNCHER.defaultIconStyle,
    // 浮球图标色。默认白;商户可在 dash 配(widget_config.launcher_icon_color)。
    launcherIconColor: '#ffffff',
    autoChatOpen: false,
    alwaysUseFloatingButton: true,
    // 面板默认整体尺寸对齐竞品区间（Tidio 372×605 / Crisp 400×730 / LiveChat 380×760）：
    // 目标 400×700。desktopHeight 是「内容区」高度，默认 header 72px → 628+72=700。
    // 视口更矮时由 widget.js 渲染期 clamp（面板总高 ≤ innerHeight-100）优雅收缩。
    desktopHeight: 628,
    desktopWidth: 400,
    // 知识库文章详情页宽度（长文行长；高度与主面板同一公式，不单独配置）
    knowDetailWidth: '580px',
    // 引用气泡的"谁说的"标签。**刻意留空**：有值会被 replyFromLabel 当成
    // 商户显式配置、压过 i18n。这里原本写死中文「您」（唯一来源、无任何 i18n
    // 分支），于是任何语言的访客引用自己的消息都印着「您」；对家 agentName 更
    // 干脆——从来没定义过，React 把 undefined 渲染成空。默认文案现在走
    // chat.reply_self / chat.reply_agent（与 Android 回落链同名）。
    visitorPronoun: null,
    agentName: null,
    // 大头部模式配置
    headerMode: 'default',    // 'default' (tabs) | 'compact' (icon+title+subtitle) | 'large' (avatars+status)
    headerAgents: [],         // [{name: 'Agent Name', avatar_url: '...'}]
    responseTimeText: '',     // e.g. "通常在几分钟内回复"
    headerPattern: 'stars',   // stars|dots|grid|waves|circles|diagonal|triangles|crosses|none
    showHeaderAvatar: true,   // 大头部模式下是否显示头像行

    // Launcher label and quick buttons. Off until the merchant configures them.
    launcherLabelEnabled: false,
    launcherLabelText: '',
    launcherLabelShowResponseTime: false,
    launcherLabelMobile: false,
    quickButtons: [],
    launcherImageUrl: '',
    logoUrl: '',
    homeLayout: 'classic',       // classic | conversation_first
    offlineStatusMode: 'auto',   // auto | response_time | business_hours
    businessHours: null,         // {start, end, timezone}, sent only for business_hours

    // ---------- Bubble teaser (Intercom/Drift 风格) ----------
    // 关闭状态下在浮动按钮旁边弹出一个气泡消息，点击可展开聊天。
    // 不设置 teaserMessage 时完全不渲染，零性能开销。
    teaserMessage: '',              // 为空 → 不显示 teaser
    teaserDelayMs: 3000,            // mount 后多久出现
    teaserAutoDismissMs: 0,         // >0 时自动消失（ms）；0 常驻
    teaserOncePerSession: true,     // 同 appId 一次 session 只展示一次（localStorage）
    teaserAvatarUrl: '',            // 可选：teaser 左侧头像
    teaserTitle: '',                // 可选：加粗标题（如客服名字）

    // ---------- Inline embed 模式 ----------
    // 不需要浮动按钮；widget 直接渲染到宿主容器里，响应式铺满其宽高。
    // 激活方式：window.laneChatMode = 'inline' + window.laneChatContainer = '#my-div'
    // 被激活时 closedStyle/alwaysUseFloatingButton 自动失效，chat 视图永远展开。
    mode: 'floating',               // 'floating' | 'inline'
    inlineMinHeight: '520px',       // inline 模式默认最小高度（容器未指定高度时）
};

// widget_config→theme 映射、mainColor 派生链（darken/gradient/rgb 纯函数）都在
// theme-color.js——那份文件零依赖（不 import ../i18n），可以被 node --test 直接
// 加载做纯逻辑测试；这个文件因为 defaultConfiguration 要用 t()，没法做到零依赖，
// 混在一起会把纯函数也拖下水。这里原样转出，widget.js 的 import 路径不用改。
export {
    WIDGET_CONFIG_THEME_KEY_MAP,
    widgetConfigToThemeUpdate,
    computeEarlyThemeUpdate,
    hexToRgbTriplet,
    colorToRgbTriplet,
    parseColor,
    formatColor,
    withAlpha,
    stripAlpha,
    sameColor,
    darkenHex,
    darkenColor,
    deriveHeaderGradient,
    resolveHeaderGradient,
    parsePositiveIntId,
    resolveHomeViewAction,
    resolveFirstVisitLandingView,
    resolveAutoOpen,
    isExplicitlySet,
    resolveInlineMinHeightFallback,
    resolveInitErrorKind,
    resolveColorKnown,
    launcherIconColor,
    neutralLauncherColor,
    parseCachedAppConfig,
    WIDGET_CONFIG_CACHE_SCHEMA_VERSION,
    // 暗色主题引擎（theme-color.js 是全产品唯一算法真源）
    THEME_MODES,
    resolveThemeMode,
    resolveEffectiveTheme,
    deriveDarkAccent,
    deriveDarkPalette,
    resolveDarkPalette,
    darkPaletteToCssVars,
    // 亮色语义色板（T1 归一：亮色也恒定义 --lc-*，与暗色对称）
    lightPaletteToCssVars,
    LIGHT_PALETTE,
    // 结构层 token（圆角/间距/阴影/动效，spec 2026-08-08）——与颜色 token 正交，
    // 只有阴影档随亮/暗换色，几何恒定
    shadowTokensToCssVars,
    structureTokensCssText,
    ensureStructureTokenStyle,
    DARK_COLOR_KEYS,
    DARK_NEUTRALS,
    relativeLuminance,
    contrastRatio,
    pickTextOn,
} from './theme-color';
